import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './theme/ThemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { I18nProvider } from './i18n/I18nProvider';
import * as Sentry from '@sentry/react';
import { silenceConsoleInProduction } from './utils/logger';
import App from './App';
import './index.css';

// Error tracking — no-op without REACT_APP_SENTRY_DSN, so local dev is unaffected.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    // The benign ResizeObserver loop warning is filtered below anyway —
    // keep it out of Sentry too.
    ignoreErrors: [/ResizeObserver loop/],
  });
}

silenceConsoleInProduction();

/**
 * Suppress the "ResizeObserver loop completed with undelivered notifications"
 * browser warning. It's benign — the spec lets the browser bail out of a
 * ResizeObserver callback that would cause an infinite re-layout, and there
 * is no application-side action to take. CRA's dev overlay treats it as a
 * fatal "Uncaught runtime error", which is alarming but wrong.
 *
 * Triggered most often by React Flow (@xyflow/react) in the bot-flow editor
 * when nodes settle after the initial layout. Filtering it here keeps the
 * overlay focused on real bugs.
 */
// Patch ResizeObserver so its callbacks fire inside requestAnimationFrame.
// This prevents the "ResizeObserver loop completed with undelivered notifications"
// warning at the source — the browser only emits it when a callback synchronously
// triggers another layout, and rAF moves the work to the next frame. React Flow
// (@xyflow/react) is the main producer of this warning in the bot-flow editor.
// We also keep an error-event filter as defense in depth for the dev overlay.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          // Bail out if the observer was disconnected between frames
          if (!Array.isArray(entries) || !entries.length) return;
          callback(entries, observer);
        });
      });
    }
  };
}

const RESIZE_OBSERVER_LOOP_RE = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener('error', (e) => {
  if (e.message && RESIZE_OBSERVER_LOOP_RE.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  const msg = (e.reason && (e.reason.message ?? String(e.reason))) || '';
  if (RESIZE_OBSERVER_LOOP_RE.test(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <App />
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  </React.StrictMode>,
);
